import { useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import Swal from 'sweetalert2';
import type { BulkSelect } from '../../hooks/useBulkSelect';

type Props = {
  bulk: BulkSelect;
  /** Ids currently visible in the list (after search/filter/paging). */
  visibleIds: string[];
  /** Singular/plural noun for messages, e.g. "status" / "statuses". */
  noun: string;
  nounPlural?: string;
  /** Deletes one item using the page's existing API call. */
  deleteOne: (id: string) => Promise<unknown>;
  /** Called after the batch finishes with the ids that were actually deleted (reload the list). */
  onDone: (deletedIds: string[]) => void | Promise<void>;
};

/** "Select all" checkbox + "Delete selected" action for listing pages. */
const BulkSelectBar = ({ bulk, visibleIds, noun, nounPlural, deleteOne, onDone }: Props) => {
  const [busy, setBusy] = useState(false);
  const plural = nounPlural || `${noun}s`;
  const selectedVisible = visibleIds.filter((id) => bulk.isSelected(id)).length;
  const allSelected = visibleIds.length > 0 && selectedVisible === visibleIds.length;
  const someSelected = selectedVisible > 0 && !allSelected;

  const handleDelete = async () => {
    const ids = visibleIds.filter((id) => bulk.isSelected(id));
    if (!ids.length) return;
    const label = ids.length === 1 ? noun : plural;
    const result = await Swal.fire({
      title: `Delete ${ids.length} ${label}?`,
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#d33',
    });
    if (!result.isConfirmed) return;

    setBusy(true);
    try {
      const results = await Promise.allSettled(ids.map((id) => deleteOne(id)));
      const failed = results.filter((r) => r.status === 'rejected').length;
      const deletedIds = ids.filter((_, i) => results[i].status === 'fulfilled');
      bulk.clear();
      await onDone(deletedIds);
      if (failed === 0) Swal.fire('Deleted', `${ids.length} ${label} deleted successfully`, 'success');
      else Swal.fire('Partly deleted', `${ids.length - failed} deleted, ${failed} failed`, 'warning');
    } finally {
      setBusy(false);
    }
  };

  if (visibleIds.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', py: 0.5 }}>
      <FormControlLabel
        sx={{ m: 0 }}
        control={
          <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={someSelected}
            onChange={(e) => bulk.setAll(visibleIds, e.target.checked)}
          />
        }
        label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Select all</Typography>}
      />
      {selectedVisible > 0 && (
        <>
          <Typography variant="body2" color="text.secondary">{selectedVisible} selected</Typography>
          <Button
            size="small"
            variant="contained"
            color="error"
            startIcon={<DeleteRounded fontSize="small" />}
            disabled={busy}
            onClick={handleDelete}
          >
            {busy ? 'Deleting…' : 'Delete selected'}
          </Button>
        </>
      )}
    </Box>
  );
};

export default BulkSelectBar;
